import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useSelector } from 'react-redux';
import type { UserOrganisation } from '../types/types';
import { captureSupabaseError } from '../lib/sentry';

interface SendAssessmentsResult {
  success: boolean;
  createdAssessments?: {
    assessment_id: string;
    supplier_id: string;
    email: string | null;
  }[];
  message?: string;
}

interface UseSendAssessmentsReturn {
  sendAssessments: (templateId: string, supplierIds: string[]) => Promise<void>;
  sending: boolean;
  error: string | null;
  result: SendAssessmentsResult | null;
  reset: () => void; // ✅ Explicitly include reset in return type
}

export function useSendAssessments(): UseSendAssessmentsReturn {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SendAssessmentsResult | null>(null);
  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected
  ) as UserOrganisation | undefined;

  const sendAssessments = useCallback(
    async (templateId: string, supplierIds: string[]) => {
      setSending(true);
      setError(null);
      setResult(null);

      try {
        const { data, error } = await supabase.functions.invoke(
          'create-assessments',
          {
            body: {
              template_id: templateId,
              supplier_ids: supplierIds,
              organisation_id: selectedOrganisation?.organisation?.id,
            },
          }
        );

        if (error) {
          setError(error.message);
          setResult({ success: false, message: error.message });
        } else {
          const parsed = typeof data === 'string' ? JSON.parse(data) : data;
          setResult(parsed);
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        captureSupabaseError(err, { op: 'assessments.create' });
        console.error('SendAssessments error:', err);
        setError(err.message ?? 'Unknown error');
        setResult({ success: false, message: err.message });
      } finally {
        setSending(false);
      }
    },
    []
  );

  /** ✅ Reset the hook state */
  const reset = useCallback(() => {
    setError(null);
    setResult(null);
  }, []);

  return { sendAssessments, sending, error, result, reset };
}
